import {
  ODataQuery,
  PartnerEndpointQueryKey,
  ITEM_ROOT,
} from "@theprelude/sdk";
import { BaseFilter, Filter } from "@/lib/filter";
import { OdataOperator } from "@/lib/odata";

export type ODataMethod = Exclude<
  OdataOperator,
  "is" | "is not" | "less than" | "greater than"
>;

export const OPERATOR_ODATA_METHOD: Record<ODataMethod, string> = {
  contains: "contains",
  matches: "matchespattern",
  "does not match": "matchespattern",
};

export const NEGATED_OPERATOR_ODATA_METHOD = new Set<ODataMethod>([
  "does not match",
]);

type IsArray<T> = T extends unknown[] ? T : never;
type IsNotArray<T> = T extends unknown[] ? never : T;

type ODataFilter<T> = NonNullable<ODataQuery<T>["filter"]>;
type ODataArrayFilter<T> = IsArray<ODataFilter<T>>;
type ODataObjectFilter<T> = IsNotArray<ODataFilter<T>>;

export function getOdataFilters<T>(filters: Filter[]): ODataArrayFilter<T> {
  return filters.map((filter) => getOdataFilter(filter));
}

function getOdataFilter<T>(filter: Filter): ODataObjectFilter<T> {
  if (filter.scope === "excepted" && filter.value === false) {
    return {
      not: [
        {
          [filter.scope]: {
            any: {
              and: [
                filter.with ?? {},
                getOperatorFilter({ ...filter, value: true }),
              ],
            },
          },
        },
      ],
    };
  }

  if (filter.scope) {
    return {
      [filter.scope]: {
        any: {
          and: [filter.with ?? {}, getConditionFilter(filter)],
        },
      },
    };
  }

  return getConditionFilter(filter);
}

const searchField = (
  field: PartnerEndpointQueryKey | string,
  value: string,
) => ({
  [field]: { contains: value },
});

export function getEndpointOdataSearchFilters(searchValue: string) {
  return {
    or: [
      {
        normalized_mac_address: {
          any: searchField(ITEM_ROOT, searchValue),
        },
      },
      searchField("normalized_hostname", searchValue),
      searchField("normalized_serial_number", searchValue),
      searchField("normalized_cloud_account_id", searchValue),
      searchField("normalized_cloud_instance_id", searchValue),
      searchField("normalized_serial_number", searchValue),
      {
        instances: {
          any: {
            or: [
              searchField("last_seen", searchValue),
              searchField("platform", searchValue),
              searchField("device_type", searchValue),
              searchField("device_ownership", searchValue),
            ],
          },
        },
      },
    ],
  };
}

export function getUserOdataSearchFilters(searchValue: string) {
  return {
    or: [
      searchField("email", searchValue),
      {
        instances: {
          any: {
            or: [
              searchField("user_type", searchValue),
              searchField("status", searchValue),
              searchField("last_seen", searchValue),
              searchField("risk", searchValue),
            ],
          },
        },
      },
    ],
  };
}

// Helper function to handle 'or' conditions or return the base operator filter
function getConditionFilter<T>(filter: Filter): ODataObjectFilter<T> {
  if (filter.ors && filter.ors.length > 0) {
    const mainCondition = getOperatorFilter(filter);
    const orConditions = filter.ors.map((or) =>
      getOperatorFilter({ ...filter, operator: or.operator, value: or.value }),
    );
    return {
      or: [mainCondition, ...orConditions],
    } as ODataObjectFilter<T>;
  } else {
    return getOperatorFilter(filter);
  }
}

function getOperatorComparator(filter: BaseFilter): object {
  const { operator, value } = filter;

  switch (operator) {
    case "is not": {
      return { ne: value };
    }
    case "is": {
      return { eq: value };
    }
    case "greater than": {
      return { gt: value };
    }
    case "less than": {
      return { lt: value };
    }
    default: {
      const methodOperator = operator as ODataMethod;
      const method = OPERATOR_ODATA_METHOD[methodOperator];
      if (method) {
        return { [method]: value };
      }
      return {};
    }
  }
}

function getOperatorFilter<T>(filter: Filter): ODataObjectFilter<T> {
  const { id, operator, fn, isArray } = filter;

  if (operator === undefined) {
    return {};
  }

  const property = fn ? `${fn}(${id})` : id;
  const comparator = getOperatorComparator(filter);

  const baseFilter = isArray
    ? { [property]: { any: { [ITEM_ROOT]: comparator } } }
    : { [property]: comparator };

  return NEGATED_OPERATOR_ODATA_METHOD.has(operator as ODataMethod)
    ? ({ not: [baseFilter] } as ODataObjectFilter<T>)
    : baseFilter;
}
